import {
  AppointmentSlotDuration,
  createPsychiatryAppointment,
  createTherapyAppointment,
  inviteNewPatient,
  determineSlotAvailability,
  AppointmentRecurring,
} from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { ProviderMedicalRole, Sex } from '@uplift/types';


export type AddSessionParams = {
  isNewPatient: boolean;
  invitedPatient?: {
    firstName: string;
    lastName: string;
    email: string;
    birthDate?: string;
    sex?: Sex;
  };

  existingPatient?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
  };

  duration?: AppointmentSlotDuration;

  date: string | null;
  startTime: string | null;

  repeats: AppointmentRecurring;
  notes?: string;
};


export const useAddSession = () => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const isPsychiatrist = me.medicalRole === ProviderMedicalRole.psychiatrist;

  const createUser = async (invitedPatientData: AddSessionParams['invitedPatient']) => {
    const result = await inviteNewPatient({
      email: invitedPatientData.email,
      first_name: invitedPatientData.firstName,
      last_name: invitedPatientData.lastName,
      birth_date: invitedPatientData.birthDate,
      sex: invitedPatientData.sex
    });

    if ('error' in result) {
      const error = result.errorKey || result.error;

      if (error) {
        throw new Error(error || '');
      }
    }

    return {
      id: result.patientId,
      preferredName: undefined,
      ...invitedPatientData,
    };
  };

  const getOrCreatePatient = async (values: AddSessionParams) => {
    if (values.isNewPatient && values.invitedPatient) {
      const newPatient = await createUser(values.invitedPatient);

      return newPatient;
    }

    if (!values.existingPatient) {
      throw new Error('Patient is not selected');
    }

    return values.existingPatient;
  };

  const createAppointment = async (patientId: string, timeStart: string, values: AddSessionParams) => {
    if (isPsychiatrist) {
      return await createPsychiatryAppointment({
        provider_id: me.id,
        patient_id: patientId,
        time_start: timeStart,
        duration: values.duration,
        notes: values.notes ?? '',
        timezone: timezone,
      });
    } else {
      return await createTherapyAppointment({
        is_working_time: false,
        is_new_patient: values.isNewPatient,
        patient_id: patientId,
        provider_id: me.id,
        notes: values.notes ?? '',
        recurring: values.repeats,
        time_start: timeStart,
        duration: values.duration,
        timezone: timezone,
      });
    }
  };

  const addSession = async (values: AddSessionParams) => {
    const time = CalendarDateService.date(values.startTime).utc();
    const timeStart = time.format();

    const { data: { slotAvailability } } = await determineSlotAvailability({
      patient_id: values?.existingPatient?.id,
      start: timeStart,
      duration: values.duration ?? AppointmentSlotDuration.sixty,
      recurring: values.repeats,
      timezone: timezone,
    });

    if (slotAvailability.available === false) {
      throw new Error(slotAvailability.error);
    }

    const patient = await getOrCreatePatient(values);
    const appointmentResponse = await createAppointment(patient.id, timeStart, values);

    if (appointmentResponse?.error) {
      throw new Error(appointmentResponse.error);
    }

    return {
      patient,
    };
  };

  return { addSession };
};
